(function () {
  "use strict";

  const SCHEMA_VERSION = 1;
  const MAX_STORED_CHARS = 4096;
  const DOCUMENT_FIELDS = Object.freeze([
    "dataLanguage",
    "locale",
    "revision",
    "schemaVersion",
    "serverDataspace",
  ]);
  const PREFERENCE_FIELDS = Object.freeze([
    "dataLanguage",
    "locale",
    "serverDataspace",
  ]);
  const BRAND = Symbol.for("fishystuff.language-preference-repository.v1");
  const CONFIG_BRAND = Symbol.for("fishystuff.language-config-contract.v1");
  const PROVIDER_SLOT = Symbol.for(
    "fishystuff.language-preference-local-provider.v1",
  );
  const LOCAL_REPOSITORY_FACTORY_SLOT = Symbol.for(
    "fishystuff.language-preference-local-repository-factory.v1",
  );
  const DEFAULT_PREFERENCES = Object.freeze({
    locale: null,
    dataLanguage: null,
    serverDataspace: null,
  });

  function isRecord(value) {
    if (!value || typeof value !== "object" || Array.isArray(value)) return false;
    const prototype = Object.getPrototypeOf(value);
    return prototype === null || Object.getPrototypeOf(prototype) === null;
  }

  function exactFields(value, expected) {
    const fields = Object.keys(value).sort();
    return fields.length === expected.length
      && fields.every((field, index) => field === expected[index]);
  }

  function freezeState(value) {
    if (!value || typeof value !== "object") return Object.freeze(value);
    const next = { ...value };
    if (next.legacyCleanup) {
      next.legacyCleanup = Object.freeze({ ...next.legacyCleanup });
    }
    return Object.freeze(next);
  }

  function unavailable(operation, reason) {
    return freezeState({ kind: "unavailable", operation, reason });
  }

  function statesEqual(left, right) {
    return JSON.stringify(left) === JSON.stringify(right);
  }

  function preferencesFrom(value) {
    return Object.freeze({
      locale: value.locale,
      dataLanguage: value.dataLanguage,
      serverDataspace: value.serverDataspace,
    });
  }

  function preferencesEqual(left, right) {
    return PREFERENCE_FIELDS.every((field) => left[field] === right[field]);
  }

  const contract = window.__fishystuffLanguageConfigV1;
  if (!contract || contract[CONFIG_BRAND] !== 1) {
    throw new TypeError("Language preference repository requires config contract v1.");
  }

  function validatePreferences(value) {
    if (!isRecord(value) || !exactFields(value, PREFERENCE_FIELDS)) {
      return { ok: false, reason: "preference-fields-must-match-v1" };
    }
    if (value.locale !== null && !contract.isLocale(value.locale)) {
      return { ok: false, reason: "locale-must-be-null-or-configured" };
    }
    if (
      value.dataLanguage !== null
      && value.dataLanguage !== contract.regionDefaultValue
      && !contract.isDataLanguage(value.dataLanguage)
    ) {
      return {
        ok: false,
        reason: "data-language-must-be-null-region-default-or-canonical",
      };
    }
    if (
      value.serverDataspace !== null
      && !contract.isServerDataspace(value.serverDataspace)
    ) {
      return {
        ok: false,
        reason: "server-dataspace-must-be-null-or-configured",
      };
    }
    return { ok: true, preferences: preferencesFrom(value) };
  }

  function requirePreferences(value) {
    const validated = validatePreferences(value);
    if (!validated.ok) {
      throw new TypeError(`Invalid language preferences: ${validated.reason}.`);
    }
    return validated.preferences;
  }

  function decodeDocument(raw) {
    if (typeof raw !== "string") {
      return freezeState({
        kind: "invalid",
        reason: "stored-value-must-be-a-string",
      });
    }
    if (raw.length > MAX_STORED_CHARS) {
      return freezeState({
        kind: "invalid",
        reason: "stored-value-exceeds-v1-limit",
      });
    }
    let document;
    try {
      document = JSON.parse(raw);
    } catch (_error) {
      return freezeState({ kind: "invalid", reason: "malformed-json" });
    }
    if (!isRecord(document)) {
      return freezeState({
        kind: "invalid",
        reason: "document-must-be-an-object",
      });
    }
    if (
      Number.isSafeInteger(document.schemaVersion)
      && document.schemaVersion > SCHEMA_VERSION
    ) {
      return freezeState({
        kind: "unsupportedVersion",
        foundVersion: document.schemaVersion,
      });
    }
    if (!exactFields(document, DOCUMENT_FIELDS)) {
      return freezeState({
        kind: "invalid",
        reason: "document-fields-must-match-v1",
      });
    }
    if (document.schemaVersion !== SCHEMA_VERSION) {
      return freezeState({
        kind: "invalid",
        reason: "schema-version-must-be-1",
      });
    }
    if (!Number.isSafeInteger(document.revision) || document.revision <= 0) {
      return freezeState({
        kind: "invalid",
        reason: "revision-must-be-a-positive-safe-integer",
      });
    }
    const validated = validatePreferences({
      locale: document.locale,
      dataLanguage: document.dataLanguage,
      serverDataspace: document.serverDataspace,
    });
    if (!validated.ok) {
      return freezeState({ kind: "invalid", reason: validated.reason });
    }
    return freezeState({
      kind: "ready",
      revision: document.revision,
      ...validated.preferences,
    });
  }

  function encodeDocument(revision, preferences) {
    if (!Number.isSafeInteger(revision) || revision <= 0) {
      throw new TypeError("Language preference revision must be positive.");
    }
    const exact = requirePreferences(preferences);
    return JSON.stringify({
      schemaVersion: SCHEMA_VERSION,
      revision,
      ...exact,
    });
  }

  function decodeLegacy(candidate) {
    function stringValue(field) {
      const entry = candidate.fields[field];
      if (!entry.present) return { ok: true, value: null };
      if (typeof entry.value !== "string") {
        return { ok: false, reason: `legacy-${field}-must-be-text` };
      }
      return { ok: true, value: entry.value };
    }

    const locale = stringValue("locale");
    if (!locale.ok) return freezeState({ kind: "invalid", reason: locale.reason });
    const localeValue = locale.value === null
      || locale.value === ""
      || locale.value === contract.autoValue
      ? null
      : locale.value;

    const dataLanguage = stringValue("dataLanguage");
    if (!dataLanguage.ok) {
      return freezeState({ kind: "invalid", reason: dataLanguage.reason });
    }
    const dataLanguageValue = dataLanguage.value === null
      || dataLanguage.value === ""
      || dataLanguage.value === contract.followContentValue
      ? null
      : dataLanguage.value;

    const serverDataspace = stringValue("serverDataspace");
    if (!serverDataspace.ok) {
      return freezeState({ kind: "invalid", reason: serverDataspace.reason });
    }
    const serverDataspaceValue = serverDataspace.value === null
      || serverDataspace.value === ""
      ? null
      : contract.canonicalServerDataspaceCode(serverDataspace.value);

    const validated = validatePreferences({
      locale: localeValue,
      dataLanguage: dataLanguageValue,
      serverDataspace: serverDataspaceValue,
    });
    return validated.ok
      ? freezeState({ kind: "ready", ...validated.preferences })
      : freezeState({ kind: "invalid", reason: `legacy-${validated.reason}` });
  }

  function expectedRevisionFrom(options) {
    if (options == null) return undefined;
    if (!isRecord(options)) {
      throw new TypeError("Language command options must be an object.");
    }
    const fields = Object.keys(options);
    if (fields.length > 1 || fields.some((field) => field !== "expectedRevision")) {
      throw new TypeError("Language command options contain unsupported fields.");
    }
    const revision = options.expectedRevision;
    if (revision === undefined || revision === null) return revision;
    if (!Number.isSafeInteger(revision) || revision <= 0) {
      throw new TypeError(
        "Expected language revision must be null or a positive safe integer.",
      );
    }
    return revision;
  }

  function createRepository(provider) {
    let currentState = freezeState({ kind: "uninitialized" });
    let disposed = false;
    let unsubscribeInvalidation = null;
    const listeners = new Set();

    function state() {
      return freezeState(currentState);
    }

    function decision() {
      const source = currentState;
      if (source.kind === "ready") {
        return freezeState({
          repositoryKind: "ready",
          revision: source.revision,
          ...preferencesFrom(source),
          canChange: true,
        });
      }
      if (source.kind === "missing") {
        return freezeState({
          repositoryKind: "missing",
          revision: null,
          ...DEFAULT_PREFERENCES,
          canChange: true,
        });
      }
      if (source.kind === "conflict") {
        return freezeState({
          repositoryKind: "conflict",
          revision: source.actualRevision,
          ...preferencesFrom(source),
          canChange: false,
        });
      }
      return freezeState({
        repositoryKind: typeof source.kind === "string"
          ? source.kind
          : "uninitialized",
        revision: null,
        ...DEFAULT_PREFERENCES,
        canChange: false,
      });
    }

    function deliver(listener) {
      try {
        listener(state());
      } catch (error) {
        globalThis.reportError?.(error);
      }
    }

    function publish(nextState) {
      const frozen = freezeState(nextState);
      if (statesEqual(currentState, frozen)) return state();
      currentState = frozen;
      if (!disposed) {
        for (const listener of listeners) deliver(listener);
      }
      return state();
    }

    function readAuthoritativeState() {
      const stored = provider.readDocument();
      if (stored.kind === "unavailable") {
        return unavailable(stored.operation, stored.reason);
      }
      return stored.raw === null
        ? freezeState({ kind: "missing" })
        : decodeDocument(stored.raw);
    }

    function withLegacyCleanup(authoritative) {
      const cleanup = provider.removeLegacy();
      if (cleanup.kind !== "unavailable") return authoritative;
      return freezeState({
        ...authoritative,
        legacyCleanup: {
          kind: "unavailable",
          operation: cleanup.operation,
          reason: cleanup.reason,
        },
      });
    }

    function writeReady(revision, preferences) {
      const raw = encodeDocument(revision, preferences);
      const written = provider.writeDocument(raw);
      if (written.kind === "unavailable") {
        return publish(unavailable(written.operation, written.reason));
      }
      return publish(withLegacyCleanup({
        kind: "ready",
        revision,
        ...preferences,
      }));
    }

    function load() {
      if (!unsubscribeInvalidation && !disposed) {
        unsubscribeInvalidation = provider.subscribeInvalidation(() => load());
      }
      const authoritative = readAuthoritativeState();
      if (authoritative.kind === "ready") {
        return publish(withLegacyCleanup(authoritative));
      }
      if (authoritative.kind !== "missing") return publish(authoritative);

      const legacy = provider.readLegacy();
      if (legacy.kind === "unavailable") {
        return publish(unavailable(legacy.operation, legacy.reason));
      }
      if (legacy.kind === "missing") return publish(authoritative);
      const imported = decodeLegacy(legacy);
      if (imported.kind !== "ready") {
        return publish({
          ...imported,
          legacySource: "ui-settings",
        });
      }
      return writeReady(1, preferencesFrom(imported));
    }

    function conflict(expectedRevision, authoritative) {
      return publish({
        kind: "conflict",
        expectedRevision,
        actualRevision: authoritative.kind === "ready"
          ? authoritative.revision
          : null,
        ...(authoritative.kind === "ready"
          ? preferencesFrom(authoritative)
          : DEFAULT_PREFERENCES),
      });
    }

    function commitPreferences(exact, expectedRevision, authoritative) {
      if (["invalid", "unsupportedVersion", "unavailable"].includes(authoritative.kind)) {
        return publish(authoritative);
      }
      if (authoritative.kind === "missing") {
        if (expectedRevision !== undefined && expectedRevision !== null) {
          return conflict(expectedRevision, authoritative);
        }
        if (preferencesEqual(exact, DEFAULT_PREFERENCES)) {
          return publish(authoritative);
        }
        return writeReady(1, exact);
      }
      if (
        expectedRevision !== undefined
        && expectedRevision !== authoritative.revision
      ) {
        if (preferencesEqual(authoritative, exact)) return publish(authoritative);
        return conflict(expectedRevision, authoritative);
      }
      if (preferencesEqual(authoritative, exact)) return publish(authoritative);
      if (authoritative.revision >= Number.MAX_SAFE_INTEGER) {
        return publish(unavailable("write", "revision-exhausted"));
      }
      return writeReady(authoritative.revision + 1, exact);
    }

    async function setPreferences(preferences, options = {}) {
      const exact = requirePreferences(preferences);
      const expectedRevision = expectedRevisionFrom(options);
      return commitPreferences(
        exact,
        expectedRevision,
        readAuthoritativeState(),
      );
    }

    async function updateField(field, value, options = {}) {
      const authoritative = readAuthoritativeState();
      if (["invalid", "unsupportedVersion", "unavailable"].includes(authoritative.kind)) {
        return publish(authoritative);
      }
      const base = authoritative.kind === "ready"
        ? preferencesFrom(authoritative)
        : DEFAULT_PREFERENCES;
      const exact = requirePreferences({ ...base, [field]: value });
      return commitPreferences(
        exact,
        expectedRevisionFrom(options),
        authoritative,
      );
    }

    function setLocale(locale, options = {}) {
      return updateField("locale", locale, options);
    }

    function setDataLanguage(dataLanguage, options = {}) {
      return updateField("dataLanguage", dataLanguage, options);
    }

    function setServerDataspace(serverDataspace, options = {}) {
      return updateField("serverDataspace", serverDataspace, options);
    }

    async function exportRecord() {
      const stored = provider.readDocument();
      if (stored.kind === "unavailable") {
        return unavailable("export", stored.reason);
      }
      if (stored.raw !== null) {
        return freezeState({
          kind: "exported",
          recordKind: decodeDocument(stored.raw).kind,
          raw: stored.raw,
        });
      }
      const legacy = provider.readLegacy();
      if (legacy.kind === "unavailable") {
        return unavailable("export", legacy.reason);
      }
      if (legacy.kind === "missing") return freezeState({ kind: "missing" });
      return freezeState({
        kind: "exported",
        recordKind: decodeLegacy(legacy).kind === "ready"
          ? "legacy"
          : "legacyInvalid",
        legacySource: "ui-settings",
        raw: legacy.raw,
      });
    }

    async function reset(options = {}) {
      const expectedRevision = expectedRevisionFrom(options);
      const authoritative = readAuthoritativeState();
      if (authoritative.kind === "unavailable") return publish(authoritative);
      if (
        authoritative.kind === "ready"
        && expectedRevision !== undefined
        && expectedRevision !== authoritative.revision
      ) {
        return conflict(expectedRevision, authoritative);
      }
      if (
        authoritative.kind === "missing"
        && expectedRevision !== undefined
        && expectedRevision !== null
      ) {
        return conflict(expectedRevision, authoritative);
      }
      const cleanup = provider.removeLegacy();
      if (cleanup.kind === "unavailable") {
        return publish(unavailable("remove", cleanup.reason));
      }
      if (authoritative.kind !== "missing") {
        const removed = provider.removeDocument();
        if (removed.kind === "unavailable") {
          return publish(unavailable(removed.operation, removed.reason));
        }
      }
      return publish({ kind: "missing" });
    }

    function subscribe(listener) {
      if (typeof listener !== "function") {
        throw new TypeError("Language repository listener must be a function.");
      }
      if (disposed) return () => {};
      listeners.add(listener);
      deliver(listener);
      return () => listeners.delete(listener);
    }

    function dispose() {
      if (disposed) return;
      disposed = true;
      unsubscribeInvalidation?.();
      unsubscribeInvalidation = null;
      listeners.clear();
      provider.dispose();
    }

    return Object.freeze({
      [BRAND]: SCHEMA_VERSION,
      decision,
      dispose,
      exportRecord,
      load,
      reset,
      setDataLanguage,
      setLocale,
      setPreferences,
      setServerDataspace,
      state,
      subscribe,
    });
  }

  const createProvider = window[PROVIDER_SLOT];
  if (typeof createProvider !== "function") {
    throw new TypeError(
      "Language preference repository requires local provider v1.",
    );
  }

  function createLocalRepository() {
    const provider = createProvider();
    if (
      !provider
      || typeof provider.dispose !== "function"
      || typeof provider.readDocument !== "function"
      || typeof provider.readLegacy !== "function"
      || typeof provider.removeDocument !== "function"
      || typeof provider.removeLegacy !== "function"
      || typeof provider.subscribeInvalidation !== "function"
      || typeof provider.writeDocument !== "function"
    ) {
      throw new TypeError(
        "Language preference repository requires local provider v1.",
      );
    }
    return createRepository(provider);
  }

  Object.defineProperty(window, LOCAL_REPOSITORY_FACTORY_SLOT, {
    configurable: true,
    value: createLocalRepository,
    writable: false,
  });
})();
